import React from 'react';
import { Routes, Route, Navigate, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ErrandProvider } from './contexts/ErrandContext';
import { ChatProvider } from './contexts/ChatContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { CourierProvider } from './contexts/CourierContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import SystemNotificationManager from './components/Notifications/SystemNotificationManager';
import { InstallPromptDash } from './components/PWA/InstallPromptDash';
import { OfflineIndicator } from './components/PWA/OfflineIndicator';
import LayoutDash from './components/Layout/LayoutDash';

// Auth Pages (needed for login)
import LoginPage from './pages/Auth/LoginPage';
import SignupPage from './pages/Auth/SignupPage';
import PhoneAuthPage from './pages/Auth/PhoneAuthPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import EmailConfirmPage from './pages/Auth/EmailConfirmPage';

// Errand Pages
import ErrandRequestPage from './pages/ErrandRequestPage';
import ErrandManagementPage from './pages/ErrandManagementPage';
import CustomerErrandTrackingPage from './pages/CustomerErrandTrackingPage';
import DriverDashboard from './pages/DriverDashboard';
import DriverApplicationForm from './components/Driver/DriverApplicationForm';

// Courier Pages
import CourierDashboard from './pages/CourierDashboard';
import CourierEarnings from './pages/CourierEarnings';

// Analytics & History
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import PaymentHistory from './pages/PaymentHistory';
import RecurringErrandsPage from './pages/RecurringErrandsPage';

// Chat Pages (for errand communication)
import ChatPage from './pages/ChatPage';

// Profile (Dash-specific, errand-only)
import ProfilePageDash from './pages/ProfilePageDash';

import SupportPage from './pages/SupportPage';

// Enhanced Home Page for Dash
import DashHomePage from './pages/DashHomePage';

// Admin Pages
import AdminConsole from './pages/AdminConsole';
import AdminUserManagement from './pages/AdminUserManagement';
import AdminCourierManagement from './pages/AdminCourierManagement';
import AdminVendorManagement from './pages/AdminVendorManagement';
import AdminDriverApplications from './pages/AdminDriverApplications';
import AdminEscrowManagement from './pages/AdminEscrowManagement';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminPaymentManagement from './pages/AdminPaymentManagement';
import AdminBookingManagement from './pages/AdminBookingManagement';
import AdminLocationsManagement from './pages/AdminLocationsManagement';
import AdminLockerManagement from './pages/AdminLockerManagement';
import AdminSupportManagement from './pages/AdminSupportManagement';
import AdminAssets from './pages/AdminAssets';

const AppDashContent: React.FC = () => {
  return (
    <LayoutDash>
      <InstallPromptDash />
      <OfflineIndicator />
      <SystemNotificationManager />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<DashHomePage />} />
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/signup" element={<SignupPage />} />
        <Route path="/auth/phone" element={<PhoneAuthPage />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/confirm" element={<EmailConfirmPage />} />
        <Route path="/driver/apply" element={<DriverApplicationForm />} />

        {/* Protected Errand Routes */}
        <Route
          path="/errand/request"
          element={
            <ProtectedRoute>
              <ErrandRequestPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/errands"
          element={
            <ProtectedRoute>
              <CustomerErrandTrackingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/driver/dashboard"
          element={
            <ProtectedRoute>
              <DriverDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/driver/errands"
          element={
            <ProtectedRoute>
              <ErrandManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courier/dashboard"
          element={
            <ProtectedRoute>
              <CourierDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courier/errands"
          element={
            <ProtectedRoute>
              <ErrandManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courier/earnings"
          element={
            <ProtectedRoute>
              <CourierEarnings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePageDash />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <AnalyticsDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payments"
          element={
            <ProtectedRoute>
              <PaymentHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/recurring"
          element={
            <ProtectedRoute>
              <RecurringErrandsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/support"
          element={
            <ProtectedRoute>
              <SupportPage />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AdminConsole />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute requireAdmin>
              <AdminUserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/couriers"
          element={
            <ProtectedRoute requireAdmin>
              <AdminCourierManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/vendors"
          element={
            <ProtectedRoute requireAdmin>
              <AdminVendorManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/driver-applications"
          element={
            <ProtectedRoute requireAdmin>
              <AdminDriverApplications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/escrow"
          element={
            <ProtectedRoute requireAdmin>
              <AdminEscrowManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute requireAdmin>
              <AdminAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/payments"
          element={
            <ProtectedRoute requireAdmin>
              <AdminPaymentManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bookings"
          element={
            <ProtectedRoute requireAdmin>
              <AdminBookingManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/locations"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLocationsManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/lockers"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLockerManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/support"
          element={
            <ProtectedRoute requireAdmin>
              <AdminSupportManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/assets"
          element={
            <ProtectedRoute requireAdmin>
              <AdminAssets />
            </ProtectedRoute>
          }
        />

        {/* Redirect unknown routes to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </LayoutDash>
  );
};

const AppDash: React.FC = () => {
  return (
    <AuthProvider>
      <ErrandProvider>
        <CourierProvider>
          <ChatProvider>
            <NotificationProvider>
              <AppDashContent />
            </NotificationProvider>
          </ChatProvider>
        </CourierProvider>
      </ErrandProvider>
    </AuthProvider>
  );
};

export default AppDash;
